import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, Clock, DollarSign } from "lucide-react";

interface SessionTimerProps {
  isActive: boolean;
  onStart: () => void;
  onPause: () => void;
  onEnd: () => void;
  startTime?: Date | null;
}

export function SessionTimer({ isActive, onStart, onPause, onEnd, startTime }: SessionTimerProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isActive && startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        setElapsed(diff);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isActive, startTime]);

  const formatTime = useCallback((seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Calculate billing units (15 min = 1 unit)
  const billingUnits = Math.ceil(elapsed / 60 / 15);
  const minutesElapsed = Math.floor(elapsed / 60);

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Session Duration</p>
              <p className="text-2xl font-mono font-bold text-foreground">
                {formatTime(elapsed)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-500/10">
              <DollarSign className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Billing Units</p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-lg font-bold">
                  {billingUnits}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  ({minutesElapsed} min)
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {!isActive ? (
              <Button onClick={onStart} size="sm" className="gap-2">
                <Play className="h-4 w-4" />
                {elapsed > 0 ? "Resume" : "Start"}
              </Button>
            ) : (
              <Button onClick={onPause} variant="outline" size="sm" className="gap-2">
                <Pause className="h-4 w-4" />
                Pause
              </Button>
            )}
            <Button onClick={onEnd} variant="destructive" size="sm" disabled={elapsed === 0}>
              End Session
            </Button>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>CPT 99213: 15-29 min (2 units)</span>
            <span>CPT 99214: 30-44 min (3 units)</span>
            <span>CPT 99215: 45+ min (4+ units)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
